var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import readyState from 'ready-state';
import { stateable } from '../decorators/stateable';
import Logger from './logger';
import { isCatalogItem, isHtmlElement } from './guards';
import { throttle } from './throttle';
/** The name of the module */
export const name = 'ShowableController';
let ShowableController = class ShowableController {
    /**
     * Create a Showable Controller component
     *
     * @param overlay Overlay instance
     */
    constructor() {
        /** Collection of showable objects */
        this.showables = new Map();
        /** Collection of triggers for showable objects */
        this.triggers = new Map();
        /**
         * Handle clicks on the DOM to check if any open showables should remain open
         *
         * @param event Mouse event
         */
        this.handleClick = throttle(50, function handleClick(event) {
            // Ignore clicks within 50ms previous to the show call, to avoid the origin click from triggering a hide event
            if (Date.now() - this.lastShowTimestamp < 50) {
                return;
            }
            // Fetch the identifier for the currently-visible showable
            const identifier = this.getState().current;
            // Is there a showable currently visible?
            if (identifier) {
                // Fetch the instance for the currently-visible showable
                const showable = this.showables.get(identifier);
                // If the showable is visible
                if (showable.isVisible() && (showable.getState().handleClick !== true)) {
                    // If the target is an HTML element and the showable instance is a catalog item
                    if (isHtmlElement(event.target) && isCatalogItem(showable)) {
                        // If the showable doesn't contain the element that was clicked on
                        if (!showable.getElement().contains(event.target)) {
                            // Hide the showable and the overlay
                            this.showables.get(identifier)._hide(); // eslint-disable-line no-underscore-dangle
                            this.hideOverlay();
                            // Update the state to reflect the now-closed Showable
                            this.setState({
                                current: null,
                                last: this.getState().current,
                            });
                        }
                    }
                }
            }
        });
        // Set the initial state
        const initialState = {
            current: null,
            last: null,
        };
        // Initialize the module's state
        this.initState(initialState);
        this.init();
    }
    async init() {
        await readyState.domready;
        // Add an event listener for clicks on the document
        document.addEventListener('click', this.handleClick.bind(this));
    }
    setOverlay(overlay) {
        if (typeof this.overlay === 'undefined') {
            this.overlay = overlay;
        }
    }
    /**
     * Register a Showable object to be controlled
     *
     * @param showable Object being registered with the controller
     */
    register(showable, triggerElement) {
        // Create an identifier for the map
        const identifier = Symbol(name);
        // Register the showable
        this.showables.set(identifier, showable);
        // Register the trigger
        this.triggers.set(identifier, triggerElement);
        return identifier;
    }
    /**
     * Request an element of a Showable object be displayed. Returns a promise that is called
     * when the element is actually shown.
     *
     * @param identifier Identifier for the Showable requesting to be shown
     */
    async requestShow(identifier, showOverlay = false) {
        // Make sure the Showable is registered
        if (!(this.showables.has(identifier))) {
            throw new Error(`Requested Showable does not exist: ${String(identifier)}`);
        }
        // Update the last show timestamp for later comparison
        this.lastShowTimestamp = Date.now();
        if (typeof this.overlay !== 'undefined') {
            if (showOverlay && !this.overlay.isVisible()) {
                // If the overlay should be visible and currently isn't, then show it
                this.showOverlay();
            }
            else if (!showOverlay && this.overlay.isVisible()) {
                // If the overlay shouldn't be visible and currently is, then hide it
                this.hideOverlay();
            }
        }
        // Fetch the identifier for the currently displayed showable
        const lastIdentifier = this.getState().current;
        // Get the showable to display
        const showable = this.showables.get(identifier);
        // Attempt to set the showable to visible
        try {
            // Show the element
            // TODO: Implement queueing system with priority to ensure that only one showable is visible at a time
            await showable._show(); // eslint-disable-line no-underscore-dangle
            // Update the current showable to be the newly requested showable
            this.setState({
                current: identifier,
            });
        }
        catch {
            Logger.warn('Unable to set Showable as visible');
        }
        // If the previously-opened showable is not the current showable
        // TODO: Add the option to allow more than one Showable to be open at a time
        if (lastIdentifier && lastIdentifier !== identifier) {
            // Update last showable to be the previous showable
            this.setState({
                last: lastIdentifier,
            });
            // Get the showable instance for the previously displayed showable
            const lastShowable = this.showables.get(lastIdentifier);
            // Hide the previous showable
            lastShowable._hide(); // eslint-disable-line no-underscore-dangle
        }
    }
    /**
     * Request an element of a Showable object be hidden. Returns a promise that is called
     * when the element is actually hidden.
     *
     * @param identifier Identifier for the Showable requesting to be hidden
     */
    async requestHide(identifier) {
        // Make sure the Showable is registered
        if (!(this.showables.has(identifier))) {
            throw new Error(`Requested Showable does not exist: ${String(identifier)}`);
        }
        // Hide the element
        // TODO: Implement queueing system to trigger showing of next showable in queue (if one exists)
        this.showables.get(identifier)._hide(); // eslint-disable-line no-underscore-dangle
        this.setState({
            current: null,
            last: this.getState().current,
        });
        this.hideOverlay();
    }
    /**
     * This allows us to cancel the hiding of the overlay if a second modal needs it
     * to stay visible.
     */
    hideOverlay() {
        if (typeof this.overlay !== 'undefined') {
            this.hideOverlayTimeoutId = window.setTimeout(() => this.overlay.hide(), 100);
        }
    }
    cancelHideOverlay() {
        window.clearTimeout(this.hideOverlayTimeoutId);
    }
    showOverlay() {
        this.cancelHideOverlay();
        if (typeof this.overlay !== 'undefined') {
            this.overlay.show();
        }
    }
};
ShowableController = __decorate([
    stateable(name)
], ShowableController);
export default ShowableController;
